import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import PostCard from "../components/PostCard";
import { Trophy, ArrowRight } from "@phosphor-icons/react";

export default function Feed() {
  const { hubKey } = useParams();
  const [posts, setPosts] = useState(null);
  const [hubs, setHubs] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      setPosts(null);
      const [pr, hr, lr, sr] = await Promise.all([
        api.get(`/posts${hubKey ? `?hub_key=${hubKey}` : ""}`),
        api.get("/hubs"),
        api.get("/leaderboard?limit=5"),
        api.get("/stats"),
      ]);
      setPosts(pr.data);
      setHubs(hr.data);
      setLeaders(lr.data);
      setStats(sr.data);
    })();
  }, [hubKey]);

  const currentHub = hubs.find((h) => h.key === hubKey);
  const bgSrc = currentHub?.background_url
    ? `${process.env.REACT_APP_BACKEND_URL}${currentHub.background_url}`
    : null;

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-8" data-testid="feed-page">
        {/* Main column */}
        <section className="lg:col-span-8">
          {currentHub ? (
            <div
              className="relative overflow-hidden border border-[#27272A] mb-8"
              style={{
                backgroundImage: bgSrc ? `url(${bgSrc})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                minHeight: 240,
              }}
              data-testid="hub-hero"
            >
              <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(10,10,12,0.92) 0%, rgba(10,10,12,0.72) 60%, rgba(10,10,12,0.4) 100%)" }} />
              <div className="absolute inset-0 blueprint-grid opacity-30" />
              <div className="relative p-6 md:p-10">
                <div className="flex items-center gap-2 mb-3">
                  <span className="hub-dot" style={{ color: currentHub.color }} />
                  <span className="label-mono" style={{ color: currentHub.color }}>HUB · {currentHub.key.toUpperCase()}</span>
                </div>
                <h1 className="font-display text-white mb-2" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 1 }}>
                  {currentHub.name}
                </h1>
                {currentHub.tagline && <p className="font-mono text-sm mb-2" style={{ color: currentHub.color }}>{currentHub.tagline}</p>}
                <p className="text-zinc-300 max-w-2xl leading-relaxed">{currentHub.description}</p>
              </div>
            </div>
          ) : (
            <div className="mb-8">
              <div className="label-mono mb-2" style={{ color: "#E60012" }}>FEED · ALL HUBS</div>
              <h1 className="font-display text-white" style={{ fontSize: 44, fontWeight: 900, letterSpacing: "-0.035em" }}>
                Signal from the hangar.
              </h1>
              <p className="text-zinc-400 mt-2">Latest posts across the iAAM hubs.</p>
            </div>
          )}

          {posts === null ? (
            <div className="text-zinc-500 label-mono py-12 text-center"><span className="spinner mr-3 align-middle" /> LOADING TELEMETRY...</div>
          ) : posts.length === 0 ? (
            <div className="border border-dashed border-[#27272A] p-12 text-center" data-testid="feed-empty">
              <div className="label-mono mb-2">NO POSTS YET</div>
              <p className="text-zinc-400">Be the first to drop a signal in this hub.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5" data-testid="feed-posts">
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}
        </section>

        {/* Right rail */}
        <aside className="lg:col-span-4 space-y-6">
          {stats && (
            <div className="card p-5 tick-corners" data-testid="feed-stats">
              <div className="label-mono mb-4" style={{ color: "#E60012" }}>LIVE COUNTERS</div>
              <div className="grid grid-cols-2 gap-px bg-[#27272A]">
                {[
                  ["users", "Members"],
                  ["posts", "Posts"],
                  ["replies", "Replies"],
                  ["hubs", "Hubs"],
                ].map(([k, l]) => (
                  <div key={k} className="bg-[#121214] p-3">
                    <div className="font-display text-2xl text-white" style={{ fontWeight: 900 }}>{stats[k]}</div>
                    <div className="label-mono mt-1" style={{ fontSize: 9 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="label-mono flex items-center gap-1.5" style={{ color: "#FFAB00" }}>
                <Trophy size={11} weight="fill" /> TOP CONTRIBUTORS
              </div>
              <Link to="/leaderboard" className="label-mono hover:text-white">VIEW ALL <ArrowRight size={9} weight="bold" className="inline" /></Link>
            </div>
            <ol className="space-y-3">
              {leaders.map((u, i) => (
                <li key={u.id}>
                  <Link to={`/profile/${u.id}`} className="flex items-center gap-3 group" data-testid={`leader-${i}`}>
                    <span className="font-display text-2xl text-zinc-700 w-7" style={{ fontWeight: 900 }}>{String(i + 1).padStart(2, "0")}</span>
                    <div className="w-8 h-8 bg-[#27272A] flex items-center justify-center text-sm font-display text-white">
                      {u.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate group-hover:underline underline-offset-4">{u.name}</div>
                      <div className="label-mono mt-0.5" style={{ fontSize: 9 }}>LVL {u.level} · {u.points} XP</div>
                    </div>
                    {u.role !== "public" && <span className={`role-badge ${u.role}`}>{u.role}</span>}
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="card p-5 bg-[#1A1A1D]">
            <div className="label-mono mb-3" style={{ color: "#00E5FF" }}>PROTOCOL</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Drop a video or voice note — iAAM auto-transcribes with Whisper and summarizes with GPT.
              Every post becomes searchable knowledge.
            </p>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
